import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useIsMobile, useBreakpoint } from "@/hooks/useMobile";
import { LayoutDashboard, LogOut, PanelLeft, Users, Scissors, CalendarDays, Settings, Home, Star, CreditCard, Percent, UserCheck, Zap, ShieldCheck, BarChart3, BookOpen, CalendarRange, Monitor, MonitorPlay, Lock, Crown, Receipt, AlertTriangle, X, Package, ShoppingBag, Loader2, Building2, MessageCircle, Brain, FlaskConical, GripVertical, RotateCcw, UserCircle2, Megaphone, FileCheck, FileText, MessageSquare, MoreHorizontal, ChevronDown, ClipboardX, TrendingUp, Instagram, Clock, ListPlus, Gift, WalletCards, PlugZap, GraduationCap, QrCode, Type, Contrast } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from './DashboardLayoutSkeleton';
import { Button } from "./ui/button";
import { SalonNotificationBell } from "./SalonNotificationBell";
import { trpc } from "@/lib/trpc";
import SuspendedPage from "@/pages/SuspendedPage";
import { WalkInModal } from "./WalkInModal";
import { useFeatures, SalonFeaturesType } from "@/contexts/FeaturesContext";
import { useSalonContext } from "@/contexts/SalonContext";
import { resolveMenuAccess } from "@/lib/menuAccess";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation";
import { GlobalSearchTrigger } from "./GlobalSearch";
import { getMobileQuickActions, type MobileQuickActionIcon } from "@/lib/mobileQuickActions";
import { getMobileOperationalAlerts } from "@shared/mobileOperations";
import { MobileNetworkRecoveryBanner } from "./MobileNetworkRecoveryBanner";

type StaffRole = "admin" | "funcionario" | "caixa" | "recepcionista";

const menuItems: { icon: React.ElementType; label: string; path: string; roles: StaffRole[]; featureKey?: keyof SalonFeaturesType }[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/", roles: ["admin", "funcionario", "caixa", "recepcionista"] },
  { icon: CalendarDays, label: "Agendamentos", path: "/appointments", roles: ["admin", "caixa", "recepcionista"] },
  { icon: Users, label: "Profissionais", path: "/professionals", roles: ["admin", "funcionario"], featureKey: "hasOperations" },
  { icon: Scissors, label: "Serviços", path: "/services", roles: ["admin", "funcionario"] },
  { icon: Package, label: "Pacotes", path: "/packages", roles: ["admin"], featureKey: "hasCrm" },
  { icon: Gift, label: "Fidelidade", path: "/loyalty", roles: ["admin"], featureKey: "hasCrm" },
  { icon: Star, label: "Avaliações", path: "/reviews", roles: ["admin"], featureKey: "hasCrm" },
  { icon: CreditCard, label: "Caixa", path: "/checkout", roles: ["admin", "caixa", "recepcionista"], featureKey: "hasCheckout" },
  { icon: WalletCards, label: "Financeiro", path: "/financial", roles: ["admin"], featureKey: "hasFinancial" },
  { icon: Percent, label: "Comissões", path: "/commissions", roles: ["admin"], featureKey: "hasCommissions" },
  { icon: Clock, label: "Controle de Ponto", path: "/attendance", roles: ["admin"], featureKey: "hasOperations" },
  { icon: BarChart3, label: "Relatórios", path: "/reports", roles: ["admin"], featureKey: "hasAdvancedReports" },
  { icon: Brain, label: "Inteligência", path: "/intelligence", roles: ["admin"], featureKey: "hasIntelligence" },
  { icon: CalendarRange, label: "Agenda Semanal", path: "/weekly-calendar", roles: ["admin", "funcionario", "recepcionista"] },
  { icon: RotateCcw, label: "Recorrências", path: "/recurrences", roles: ["admin", "recepcionista"], featureKey: "hasCrm" },
  { icon: UserCheck, label: "Clientes", path: "/clients", roles: ["admin"], featureKey: "hasCrm" },
  { icon: FileCheck, label: "Prontuários", path: "/client-records", roles: ["admin"], featureKey: "hasCrm" },
  { icon: ShieldCheck, label: "Equipe", path: "/staff", roles: ["admin"], featureKey: "hasOperations" },
  { icon: Settings, label: "Configurações", path: "/settings", roles: ["admin"] },
  { icon: Monitor, label: "Painel de Chamada", path: "/painel-chamada", roles: ["admin", "caixa", "recepcionista"], featureKey: "hasPanelChamada" },
  { icon: Megaphone, label: "Promoções", path: "/promotions", roles: ["admin"], featureKey: "hasCrm" },
  { icon: Users, label: "CRM", path: "/crm", roles: ["admin"], featureKey: "hasCrm" },
  { icon: MonitorPlay, label: "Mídia Promocional", path: "/media", roles: ["admin"], featureKey: "hasMediaPromocional" },
  { icon: BookOpen, label: "Tutorial", path: "/tutorial", roles: ["admin", "funcionario", "caixa", "recepcionista"], featureKey: "hasAcademy" },
  { icon: GraduationCap, label: "Academia", path: "/academia", roles: ["admin", "funcionario", "caixa", "recepcionista"], featureKey: "hasAcademy" },
  { icon: Crown, label: "Upgrade de Plano", path: "/upgrade", roles: ["admin"] },
  { icon: Receipt, label: "Pagamentos", path: "/payments", roles: ["admin"], featureKey: "hasFinancial" },
  { icon: Package, label: "Produtos", path: "/products", roles: ["admin", "caixa"], featureKey: "hasInventory" },
  { icon: ShoppingBag, label: "Pedidos da Loja", path: "/store-orders", roles: ["admin", "caixa"], featureKey: "hasInventory" },
  { icon: Building2, label: "Filiais", path: "/branches", roles: ["admin"], featureKey: "hasBranches" },
  { icon: MessageCircle, label: "Central Omnichannel", path: "/ai-conversations", roles: ["admin", "recepcionista"], featureKey: "hasAutomation" },
  { icon: Brain, label: "Base de Conhecimento", path: "/knowledge", roles: ["admin"], featureKey: "hasAutomation" },
  { icon: FlaskConical, label: "Teste do Agente", path: "/agent-test", roles: ["admin"], featureKey: "hasAutomation" },
  { icon: FileCheck, label: "Comprovantes PIX", path: "/payment-proofs", roles: ["admin"], featureKey: "hasFinancial" },
  { icon: FileText, label: "Notas Fiscais", path: "/fiscal", roles: ["admin"], featureKey: "hasFiscal" },
  { icon: MessageSquare, label: "Auditoria WhatsApp", path: "/whatsapp-logs", roles: ["admin"], featureKey: "hasAutomation" },
  { icon: AlertTriangle, label: "Automações", path: "/automation-monitor", roles: ["admin"], featureKey: "hasAutomation" },
  { icon: Lock, label: "Segurança", path: "/privacy-security", roles: ["admin"], featureKey: "hasSecurity" },
  { icon: PlugZap, label: "Integrações", path: "/integrations", roles: ["admin"], featureKey: "hasIntegrations" },
  { icon: Instagram, label: "Instagram", path: "/instagram", roles: ["admin"], featureKey: "hasIntegrations" },
  { icon: UserCircle2, label: "Meu Perfil", path: "/meu-perfil", roles: ["admin", "funcionario", "caixa", "recepcionista"] },
  { icon: ClipboardX, label: "Cancelamentos", path: "/cancellation-requests", roles: ["admin"] },
  { icon: ListPlus, label: "Lista de Espera", path: "/waitlist", roles: ["admin", "recepcionista"] },
  { icon: TrendingUp, label: "Minhas Comissões", path: "/my-commissions", roles: ["funcionario"] },
  { icon: Clock, label: "Bater Ponto", path: "/clock-in", roles: ["funcionario", "caixa", "recepcionista"] },
];

type MenuGroupId = "business" | "schedule" | "customers" | "financial" | "operations" | "channels" | "administration" | "routine";

type MenuGroup = {
  id: MenuGroupId;
  label: string;
  icon: React.ElementType;
  paths: string[];
  defaultOpen?: boolean;
};

const MENU_GROUPS: MenuGroup[] = [
  { id: "schedule", label: "Agenda e atendimento", icon: CalendarDays, paths: ["/weekly-calendar", "/appointments", "/recurrences", "/waitlist", "/cancellation-requests", "/painel-chamada", "/ai-conversations"] },
  { id: "business", label: "Visão do negócio", icon: LayoutDashboard, paths: ["/", "/intelligence", "/reports", "/branches"] },
  { id: "customers", label: "Clientes e vendas", icon: Users, paths: ["/clients", "/crm", "/loyalty", "/packages", "/reviews", "/promotions", "/media"] },
  { id: "financial", label: "Financeiro e comercial", icon: WalletCards, paths: ["/checkout", "/financial", "/commissions", "/payments", "/payment-proofs", "/fiscal"] },
  { id: "operations", label: "Operação do salão", icon: Scissors, paths: ["/services", "/products", "/store-orders", "/professionals", "/staff", "/attendance"] },
  { id: "channels", label: "Canais, IA e automações", icon: MessageCircle, paths: ["/instagram", "/knowledge", "/agent-test", "/whatsapp-logs", "/automation-monitor"] },
  { id: "administration", label: "Administração", icon: Settings, paths: ["/settings", "/integrations", "/privacy-security", "/tutorial", "/academia", "/meu-perfil", "/upgrade"] },
  { id: "routine", label: "Minha rotina", icon: UserCheck, paths: ["/my-commissions", "/clock-in"] },
];

type MobileMoreMenuGroup = {
  id: string;
  label: string;
  icon: React.ElementType;
  paths: string[];
};

// A categorização móvel melhora a leitura sem mudar os itens liberados por cargo/plano.
const MOBILE_MORE_MENU_GROUPS: MobileMoreMenuGroup[] = [
  { id: "schedule", label: "Agenda e atendimento", icon: CalendarDays, paths: ["/weekly-calendar", "/appointments", "/recurrences", "/waitlist", "/cancellation-requests", "/painel-chamada"] },
  { id: "customers", label: "Clientes e relacionamento", icon: Users, paths: ["/clients", "/client-records", "/crm", "/loyalty", "/packages", "/reviews", "/promotions", "/media"] },
  { id: "management", label: "Gestão financeira", icon: WalletCards, paths: ["/", "/intelligence", "/reports", "/checkout", "/financial", "/commissions", "/payments", "/payment-proofs", "/fiscal", "/branches"] },
  { id: "operations", label: "Operação do salão", icon: Scissors, paths: ["/services", "/products", "/store-orders", "/professionals", "/staff", "/attendance"] },
  { id: "channels", label: "Canais, IA e automações", icon: MessageCircle, paths: ["/instagram", "/ai-conversations", "/knowledge", "/agent-test", "/whatsapp-logs", "/automation-monitor"] },
  { id: "settings", label: "Configurações e suporte", icon: Settings, paths: ["/settings", "/integrations", "/privacy-security", "/tutorial", "/academia", "/meu-perfil", "/upgrade"] },
  { id: "routine", label: "Minha rotina", icon: UserCheck, paths: ["/my-commissions", "/clock-in"] },
];

const ACCESSIBILITY_PREFERENCES_KEY = "accessibility-preferences-v1";
const ACCESSIBILITY_STYLE_ID = "dashboard-accessibility-preferences";

type AccessibilityPreferences = {
  largeText: boolean;
  highContrast: boolean;
};

function readAccessibilityPreferences(): AccessibilityPreferences {
  if (typeof window === "undefined") return { largeText: false, highContrast: false };

  try {
    const saved = localStorage.getItem(ACCESSIBILITY_PREFERENCES_KEY);
    if (!saved) return { largeText: false, highContrast: false };
    const parsed = JSON.parse(saved) as Partial<AccessibilityPreferences>;
    return { largeText: parsed.largeText === true, highContrast: parsed.highContrast === true };
  } catch {
    return { largeText: false, highContrast: false };
  }
}

function useAccessibilityPreferences() {
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(readAccessibilityPreferences);

  useEffect(() => {
    if (!document.getElementById(ACCESSIBILITY_STYLE_ID)) {
      const style = document.createElement("style");
      style.id = ACCESSIBILITY_STYLE_ID;
      style.textContent = `
        html.accessibility-text-large { font-size: var(--accessibility-text-size, 112.5%); }
        html.accessibility-high-contrast { filter: contrast(var(--accessibility-contrast, 1.45)); }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("accessibility-text-large", preferences.largeText);
    root.classList.toggle("accessibility-high-contrast", preferences.highContrast);
    root.style.setProperty("--accessibility-text-size", preferences.largeText ? "112.5%" : "100%");
    root.style.setProperty("--accessibility-contrast", preferences.highContrast ? "1.45" : "1");

    try {
      localStorage.setItem(ACCESSIBILITY_PREFERENCES_KEY, JSON.stringify(preferences));
    } catch {
      // Sem persistência disponível, as preferências permanecem ativas durante a sessão.
    }
  }, [preferences]);

  const toggleLargeText = useCallback(() => {
    setPreferences(current => ({ ...current, largeText: !current.largeText }));
  }, []);
  const toggleHighContrast = useCallback(() => {
    setPreferences(current => ({ ...current, highContrast: !current.highContrast }));
  }, []);

  return { preferences, toggleLargeText, toggleHighContrast };
}

const SIDEBAR_GROUPS_KEY = "sidebar-open-groups-v2";

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const SIDEBAR_ORDER_KEY = "sidebar-menu-order";
const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

// Componente de item sortável da sidebar
function SortableSidebarItem({
  item,
  isActive,
  isLocked,
  isCollapsed,
  draggable,
  badge,
  onClick,
}: {
  item: typeof menuItems[number];
  isActive: boolean;
  isLocked: boolean;
  isCollapsed: boolean;
  draggable: boolean;
  badge?: number;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.path, disabled: !draggable });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <SidebarMenuItem ref={setNodeRef} style={style}>
      <div className="flex items-center group/sortable">
        {!isCollapsed && draggable && (
          <button
            {...attributes}
            {...listeners}
            className="flex items-center justify-center h-10 w-5 shrink-0 opacity-0 group-hover/sortable:opacity-40 hover:!opacity-80 transition-opacity cursor-grab active:cursor-grabbing focus:outline-none"
            aria-label="Arrastar para reordenar"
            tabIndex={-1}
          >
            <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
        <SidebarMenuButton
          isActive={isActive}
          onClick={onClick}
          tooltip={isLocked ? `${item.label} — Bloqueado no seu plano` : item.label}
          className={`h-10 transition-all font-normal flex-1 ${isLocked ? "opacity-60" : ""}`}
        >
          <item.icon
            className={`h-4 w-4 ${isActive ? "text-primary" : ""} ${isLocked ? "text-muted-foreground" : ""}`}
          />
          <span className="flex-1">{item.label}</span>
          {badge !== undefined && badge > 0 && !isCollapsed && (
            <span className="ml-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
          {isLocked && !isCollapsed && (
            <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
          )}
        </SidebarMenuButton>
      </div>
    </SidebarMenuItem>
  );
}

// Banner de impersonation (Super Admin acessando como salão)
function ImpersonationBanner() {
  const [active, setActive] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const hasBackup = document.cookie.split(";").some(c => c.trim().startsWith("app_impersonation_backup="));
    setActive(hasBackup);
  }, []);

  if (!active) return null;

  const handleExit = async () => {
    setExiting(true);
    try {
      const res = await fetch("/api/impersonate/exit", { method: "POST", credentials: "include" });
      if (res.ok) {
        window.location.href = "/superadmin";
      } else {
        setExiting(false);
      }
    } catch {
      setExiting(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b bg-amber-500/10 border-amber-500/30 text-sm">
      <div className="flex items-center gap-2 text-amber-400 min-w-0">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        <span className="font-medium truncate">
          Você está visualizando este salão como <strong>Super Admin</strong>. Suas ações afetam a conta real.
        </span>
      </div>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors bg-amber-600 hover:bg-amber-700 shrink-0 flex items-center gap-1.5"
      >
        {exiting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
        Sair da sessão
      </button>
    </div>
  );
}

// Banner de trial expirando
function TrialExpiryBanner({ salon }: { salon?: { status: string; trialEndsAt?: Date | null } | null }) {
  const [dismissed, setDismissed] = useState(false);
  const [, setLocation] = useLocation();

  if (dismissed || !salon || salon.status !== "trial" || !salon.trialEndsAt) return null;

  const now = new Date();
  const trialEnd = new Date(salon.trialEndsAt);
  const diffMs = trialEnd.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // Mostrar apenas se expirar em 7 dias ou menos (e ainda não expirou)
  if (diffDays > 7 || diffDays <= 0) return null;

  const urgency = diffDays <= 2 ? "red" : diffDays <= 4 ? "orange" : "yellow";
  const bgClass = urgency === "red" ? "bg-red-500/10 border-red-500/30" : urgency === "orange" ? "bg-orange-500/10 border-orange-500/30" : "bg-yellow-500/10 border-yellow-500/30";
  const textClass = urgency === "red" ? "text-red-400" : urgency === "orange" ? "text-orange-400" : "text-yellow-400";
  const btnClass = urgency === "red" ? "bg-red-500 hover:bg-red-600" : urgency === "orange" ? "bg-orange-500 hover:bg-orange-600" : "bg-yellow-500 hover:bg-yellow-600";

  const daysLabel = diffDays === 1 ? "1 dia" : `${diffDays} dias`;

  return (
    <div className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b ${bgClass} text-sm`}>
      <div className={`flex items-center gap-2 ${textClass} min-w-0`}>
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="font-medium truncate">
          Seu período de teste expira em <strong>{daysLabel}</strong>. Faça upgrade para continuar usando o sistema.
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => setLocation("/upgrade")}
          className={`text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${btnClass}`}
        >
          Ver Planos
        </button>
        <button
          onClick={() => setDismissed(true)}
          className={`${textClass} hover:opacity-70 transition-opacity`}
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Verifica se o trial expirou (status = trial e trialEndsAt no passado)
function isTrialExpired(salon: { status: string; trialEndsAt?: Date | null } | null | undefined): boolean {
  if (!salon) return false;
  if (salon.status !== "trial") return false;
  if (!salon.trialEndsAt) return false;
  return new Date(salon.trialEndsAt) < new Date();
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  // Detectar breakpoint inicial para definir estado da sidebar
  const initialWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
  const initialSidebarOpen = initialWidth >= 1024; // desktop: aberta; tablet/mobile: fechada
  const { loading, user } = useAuth();
  const { salon: mySalon, isLoading: loadingSalon } = useSalonContext();
  // Flag persistente para evitar loop de autoLink após reload
  // Inicializa como false; o useEffect abaixo sincroniza quando user?.id estiver disponível
  const [autoLinkDone, setAutoLinkDone] = useState(false);
  const [autoLinkFailed, setAutoLinkFailed] = useState(false);
  const autoLinkMutation = (trpc as any).staff.autoLinkByEmail.useMutation({
    onSuccess: (data: any) => {
      setAutoLinkDone(true);
      if (data?.success) {
        // Salvar flag no localStorage ANTES do reload para não entrar em loop
        if (user?.id) localStorage.setItem(`autolink_done_${user.id}`, "1");
        // Só recarregar se foi uma vinculação nova (não se já estava vinculado)
        if (!data?.alreadyLinked) {
          window.location.reload();
        }
      } else {
        setAutoLinkFailed(true);
      }
    },
    onError: () => {
      setAutoLinkDone(true);
      setAutoLinkFailed(true);
    },
  });

  // Sincronizar autoLinkDone com userId quando user carregar
  useEffect(() => {
    if (user?.id) {
      const done = localStorage.getItem(`autolink_done_${user.id}`) === "1";
      if (done) setAutoLinkDone(true);
    }
  }, [user?.id]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  // Redirecionar superadmin para /superadmin se tentar acessar /admin
  useEffect(() => {
    if (!loading && user?.role === "superadmin") {
      window.location.href = "/superadmin";
    }
  }, [loading, user]);

  // Vinculação automática: quando usuário não-admin faz login, tenta vincular pelo e-mail
  // Só executa se ainda não foi feito (flag no localStorage persiste entre reloads)
  useEffect(() => {
    if (!loading && user && user.role === "user" && !autoLinkDone && !autoLinkMutation.isPending) {
      autoLinkMutation.mutate({});
    }
  }, [loading, user, autoLinkDone]);

  // Aguardar apenas a autenticação (rápida, local JWT) — não bloquear por getMySalon
  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  // Enquanto redireciona superadmin, mostrar skeleton
  if (user?.role === "superadmin") {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-6">
            <h1 className="text-2xl font-semibold tracking-tight text-center">
              Acesso restrito
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Escolha o tipo de acesso correspondente ao seu cadastro.
            </p>
          </div>
          <div className="grid w-full gap-3">
            <Button
              onClick={() => {
                window.location.href = "/team-login";
              }}
              size="lg"
              className="w-full shadow-lg hover:shadow-xl transition-all"
            >
              Acesso de profissionais e equipe
            </Button>
            <Button
              onClick={() => {
                window.location.href = "/login";
              }}
              size="lg"
              variant="outline"
              className="w-full"
            >
              Acesso administrativo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Usuário com role "user" (funcionário/caixa): aguardar vinculação automática
  if (user.role === "user" && !autoLinkDone && autoLinkMutation.isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4 p-8">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Vinculando sua conta ao salão...</p>
        </div>
      </div>
    );
  }

  // Falha na vinculação: e-mail não encontrado em nenhum salão
  if (user.role === "user" && autoLinkFailed) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-6 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
          <div>
            <h1 className="text-xl font-semibold mb-2">Conta não vinculada</h1>
            <p className="text-sm text-muted-foreground">
              Seu e-mail <strong>{user.email}</strong> não foi encontrado em nenhum salão.
              Peça ao administrador do salão para cadastrar seu e-mail corretamente na tela de Equipe.
            </p>
          </div>
          <Button variant="outline" onClick={() => window.location.href = "/"}>
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  // Bloqueio de salões inadimplentes — só aplica quando mySalon já carregou (não bloqueia se ainda carregando)
  if (user.role !== "superadmin" && mySalon && !loadingSalon) {
    if (mySalon.status === "suspended") {
      return <SuspendedPage reason="suspended" />;
    }
    if (mySalon.status === "cancelled") {
      return <SuspendedPage reason="cancelled" />;
    }
    if (isTrialExpired(mySalon)) {
      return <SuspendedPage reason="trial_expired" trialEndsAt={mySalon.trialEndsAt} />;
    }
  }

  return (
    <SidebarProvider
      defaultOpen={initialSidebarOpen}
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function WalkInButton({ salonId, isMobile }: { salonId?: number; isMobile: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm"
        className={`gap-1.5 h-10 text-sm font-medium ${isMobile ? "hidden" : "flex"}`}
        onClick={() => setOpen(true)}
        disabled={!salonId}
      >
        <Zap className="w-3.5 h-3.5" />
        Atendimento Rápido
      </Button>
      <WalkInModal open={open} onClose={() => setOpen(false)} salonId={salonId} />
    </>
  );
}

function QuickActionSheet({ salonId, onNavigate }: { salonId?: number; onNavigate: (path: string) => void }) {
  const [open, setOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [checkInQrOpen, setCheckInQrOpen] = useState(false);
  const { isMobile } = useBreakpoint();
  const goTo = (path: string) => { setOpen(false); onNavigate(path); };

  const actions = [
    { label: "Novo agendamento", description: "Abrir a agenda para criar um horário", path: "/weekly-calendar", icon: CalendarDays, tone: "bg-amber-100 text-amber-700" },
    { label: "Bloquear horário", description: "Reservar um período na agenda", path: "/weekly-calendar", icon: Lock, tone: "bg-slate-100 text-slate-700" },
    { label: "Novo cliente", description: "Acessar o cadastro de clientes", path: "/clients", icon: Users, tone: "bg-blue-100 text-blue-700" },
    { label: "Receber pagamento", description: "Ver atendimentos pendentes no caixa", path: "/checkout", icon: CreditCard, tone: "bg-emerald-100 text-emerald-700" },
  ];

  return (
    <>
      <Button variant="outline" size="icon" className={`h-10 w-10 ${isMobile ? "flex" : "hidden"}`} onClick={() => setOpen(true)} aria-label="Abrir ações rápidas" title="Ações rápidas">
        <Zap className="h-4 w-4 text-primary" />
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2 text-base"><Zap className="h-4 w-4 text-primary" />Ações rápidas</SheetTitle>
          </SheetHeader>
          <p className="mt-1 text-sm text-muted-foreground">Atalhos para as tarefas mais comuns do seu turno.</p>
          <div className="mt-4 grid grid-cols-1 gap-2">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <button key={action.label} type="button" onClick={() => goTo(action.path)} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3.5 text-left transition-colors hover:bg-accent/40">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${action.tone}`}><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0"><span className="block text-sm font-medium">{action.label}</span><span className="block text-xs text-muted-foreground">{action.description}</span></span>
                </button>
              );
            })}
            <Button className="mt-1 h-11 w-full gap-2" onClick={() => { setOpen(false); setWalkInOpen(true); }} disabled={!salonId}>
              <Zap className="h-4 w-4" />Atendimento rápido de balcão
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <WalkInModal open={walkInOpen} onClose={() => setWalkInOpen(false)} salonId={salonId} />
    </>
  );
}

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { preferences: accessibilityPreferences, toggleLargeText, toggleHighContrast } = useAccessibilityPreferences();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_GROUPS_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return Object.fromEntries(MENU_GROUPS.map(group => [group.id, !!group.defaultOpen]));
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const activeMenuItem = menuItems.find(item => item.path === location);
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  // Em celular em retrato, inclusive aparelhos Android que expõem largura CSS de tablet,
  // a navegação é a barra inferior; a sidebar nunca deve ocupar a tela.
  // Auto-colapsar sidebar apenas em tablets reais.
  useEffect(() => {
    if (isTablet && state === "expanded") {
      toggleSidebar();
    } else if (isDesktop && state === "collapsed" && !isMobile) {
      // Não forçar abertura em desktop — respeitar preferência do usuário
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTablet, isDesktop]);
  const { data: meData } = trpc.auth.me.useQuery();
  const { data: staffCtx } = trpc.staff.myContext.useQuery(undefined, {
    enabled: !!user,
  });
  const { hasFeature } = useFeatures();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);
  // Registrar notificações push para a equipe autenticada (solicita permissão uma vez)
  usePushNotifications();
  // staffRole: admin para donos de salão, ou o role do registro na tabela salon_staff
  const staffRole: StaffRole = (staffCtx?.staffRole as StaffRole) ?? "admin";
  const tutorialProgressQuery = trpc.tutorial.getMyProgress.useQuery(
    { profile: staffRole },
    { enabled: !!user && location === "/" && hasFeature("hasAcademy") },
  );
  const hasTutorialProgress = (tutorialProgressQuery.data?.completedSectionIds.length ?? 0) > 0
    || !!tutorialProgressQuery.data?.lastSectionId;
  const openTutorialContinuation = () => {
    try { sessionStorage.setItem("tutorial-resume-profile", staffRole); } catch { /* navegação continua disponível */ }
    setLocation("/tutorial");
  };
  // Permissões individuais (overrides por funcionário)
  const { data: effectivePerms } = (trpc as any).staffPerms.myEffectivePermissions.useQuery(
    undefined,
    { enabled: !!user && staffRole !== "admin" }
  );
  // Contagem de solicitações de cancelamento pendentes (apenas para admin)
  const { data: pendingCancelData } = (trpc as any).cancellationRequests.countPending.useQuery(
    undefined,
    { enabled: staffRole === "admin", refetchInterval: 60000 }
  );
  const pendingCancelCount: number = pendingCancelData?.count ?? 0;

  // Mapeamento de path -> chave de módulo de permissão
  const PATH_TO_MODULE: Record<string, string> = {
    "/appointments": "agenda",
    "/weekly-calendar": "agenda",
    "/checkout": "caixa",
    "/clients": "clientes",
    "/services": "servicos",
    "/products": "produtos",
    "/reports": "relatorios",
    "/staff": "equipe",
    "/settings": "configuracoes",
    "/promotions": "promocoes",
    "/commissions": "comissoes",
    "/my-commissions": "comissoes",
    "/professionals": "profissionais",
    "/branches": "filiais",
    "/reviews": "avaliacoes",
    "/packages": "pacotes",
  };

  const baseVisibleItems = resolveMenuAccess(menuItems, staffRole, effectivePerms?.modules, hasFeature).map(({ item }) => item);

  // Mutation para salvar ordem no banco
  const saveMenuOrderMutation = trpc.staff.saveMenuOrder.useMutation();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ordem: inicializa do localStorage (rápido), depois sincroniza com o banco
  const defaultOrder = menuItems.map(m => m.path);
  const [menuOrder, setMenuOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_ORDER_KEY);
      if (saved) {
        const parsed: string[] = JSON.parse(saved);
        const validPaths = new Set(menuItems.map(m => m.path));
        if (parsed.every(p => validPaths.has(p))) return parsed;
      }
    } catch { /* ignore */ }
    return defaultOrder;
  });

  // Sincronizar com o banco quando meData carregar
  useEffect(() => {
    const dbOrder = (meData as any)?.menuOrder;
    if (!dbOrder) return;
    try {
      const parsed: string[] = JSON.parse(dbOrder);
      const validPaths = new Set(menuItems.map(m => m.path));
      if (parsed.length > 0 && parsed.every(p => validPaths.has(p))) {
        setMenuOrder(parsed);
        localStorage.setItem(SIDEBAR_ORDER_KEY, dbOrder);
      }
    } catch { /* ignore */ }
  }, [meData]);

  const saveOrder = useCallback((order: string[]) => {
    setMenuOrder(order);
    localStorage.setItem(SIDEBAR_ORDER_KEY, JSON.stringify(order));
    // Debounce: salvar no banco após 1.5s sem novos drags
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveMenuOrderMutation.mutate({ order });
    }, 1500);
  }, [saveMenuOrderMutation]);

  const resetOrder = useCallback(() => {
    const def = menuItems.map(m => m.path);
    setMenuOrder(def);
    localStorage.setItem(SIDEBAR_ORDER_KEY, JSON.stringify(def));
    saveMenuOrderMutation.mutate({ order: def });
  }, [saveMenuOrderMutation]);

  // Ordenar visibleMenuItems conforme menuOrder
  const visibleMenuItems = [...baseVisibleItems].sort((a, b) => {
    const ai = menuOrder.indexOf(a.path);
    const bi = menuOrder.indexOf(b.path);
    const aIdx = ai === -1 ? 9999 : ai;
    const bIdx = bi === -1 ? 9999 : bi;
    return aIdx - bIdx;
  });

  const groupedMenuItems = MENU_GROUPS
    .map(group => ({
      ...group,
      items: visibleMenuItems.filter(item => group.paths.includes(item.path)),
    }))
    .filter(group => group.items.length > 0);

  const toggleGroup = useCallback((groupId: MenuGroupId) => {
    setOpenGroups(current => ({ ...current, [groupId]: !current[groupId] }));
  }, []);

  // Sensores DnD
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event: DragEndEvent, groupItems: typeof menuItems) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = groupItems.findIndex(m => m.path === active.id);
    const newIndex = groupItems.findIndex(m => m.path === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reorderedGroup = arrayMove(groupItems, oldIndex, newIndex);
    const groupPaths = new Set(groupItems.map(item => item.path));
    let groupIndex = 0;
    const newOrder = menuOrder.map(path => groupPaths.has(path) ? reorderedGroup[groupIndex++].path : path);
    saveOrder(newOrder);
  }, [menuOrder, saveOrder]);

  const canReorderMenu = staffRole === "admin" && !isCollapsed;

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  return (
    <>
      <div className={`relative ${isMobile ? "hidden" : ""}`} ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          <SidebarHeader className="h-16 justify-center">
            <div className="flex items-center gap-3 px-2 transition-all w-full">
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                aria-label="Toggle navigation"
              >
                <PanelLeft className="h-4 w-4 text-muted-foreground" />
              </button>
              {!isCollapsed ? (
                <div className="flex items-center gap-2 min-w-0">
                  <img
                    src="https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/beleza-bronze-logo-dark_41784aae.png"
                    alt="Beleza & Bronze"
                    className="h-7 w-auto object-contain"
                  />
                </div>
              ) : (
                <img
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/beleza-bronze-logo-icon_e4b47c0e.png"
                  alt="B&B"
                  className="h-7 w-7 object-contain rounded-lg"
                />
              )}
            </div>
          </SidebarHeader>

          <SidebarContent className="gap-0 pb-2">
            {groupedMenuItems.map(group => {
              const GroupIcon = group.icon;
              const groupIsOpen = isCollapsed || openGroups[group.id];
              const hasActiveItem = group.items.some(item => item.path === location);
              return (
                <section key={group.id} className="px-2 pt-1.5">
                  {!isCollapsed && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      className={`flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors hover:bg-accent/70 ${hasActiveItem ? "text-primary" : "text-muted-foreground"}`}
                      aria-expanded={groupIsOpen}
                    >
                      <GroupIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="flex-1 truncate">{group.label}</span>
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${groupIsOpen ? "rotate-0" : "-rotate-90"}`} />
                    </button>
                  )}
                  {groupIsOpen && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleDragEnd(event, group.items)}
                    >
                      <SortableContext items={group.items.map(item => item.path)} strategy={verticalListSortingStrategy}>
                        <SidebarMenu className="py-0.5">
                          {group.items.map(item => {
                            const isActive = location === item.path;
                            const isLocked = item.featureKey ? !hasFeature(item.featureKey) : false;
                            const badge = item.path === "/cancellation-requests" && pendingCancelCount > 0 ? pendingCancelCount : undefined;
                            return (
                              <SortableSidebarItem
                                key={item.path}
                                item={item}
                                isActive={isActive}
                                isLocked={isLocked}
                                isCollapsed={isCollapsed}
                                draggable={canReorderMenu}
                                badge={badge}
                                onClick={() => setLocation(item.path)}
                              />
                            );
                          })}
                        </SidebarMenu>
                      </SortableContext>
                    </DndContext>
                  )}
                </section>
              );
            })}
            {/* Botão de reset da ordem — só visível quando sidebar expandida */}
            {!isCollapsed && (
              <div className="px-3 pb-2">
                <button
                  onClick={resetOrder}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1 px-2 rounded-md hover:bg-accent/50 w-full"
                  title="Restaurar ordem padrão do menu"
                >
                  <RotateCcw className="h-3 w-3" />
                  <span>Restaurar ordem padrão</span>
                </button>
              </div>
            )}
          </SidebarContent>

          <SidebarFooter className="p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border shrink-0">
                    <AvatarFallback className="text-xs font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-medium truncate leading-none">
                      {user?.name || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-1.5">
                      {user?.email || "-"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset className="h-svh flex flex-col overflow-hidden">
        {/* Header sticky — mobile, tablet e desktop */}
        <div className="flex border-b h-14 items-center justify-between bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
          <div className="flex items-center gap-2 min-w-0">
            {/* Tablet: mostrar trigger da sidebar + título */}
            {isTablet && (
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none shrink-0"
                aria-label="Toggle navigation"
              >
                <PanelLeft className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            {isMobile ? (
              <div className="flex items-center gap-2.5">
                <img
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/beleza-bronze-logo-icon_e4b47c0e.png"
                  alt="Logo"
                  className="h-7 w-7 rounded-lg object-cover shrink-0 shadow-sm"
                />
                <div>
                  <p className="text-xs text-muted-foreground leading-none">Beleza &amp; Bronze</p>
                  <p className="text-sm font-semibold tracking-tight text-foreground leading-tight">
                    {activeMenuItem?.label ?? "Menu"}
                  </p>
                </div>
              </div>
            ) : (
              <span className="text-sm font-medium tracking-tight text-foreground truncate">
                {activeMenuItem?.label ?? "Menu"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Busca global — sempre visível */}
            <GlobalSearchTrigger />
            <QuickActionSheet salonId={meData?.salon?.id} onNavigate={setLocation} />
            {meData?.salon?.slug && (
              <Button
                variant="ghost"
                size="sm"
                className={`gap-1.5 text-muted-foreground hover:text-foreground h-8 px-2 ${isMobile ? "hidden" : "flex"}`}
                onClick={() => window.open(`/s/${meData.salon!.slug}/loja`, "_blank")}
                title="Ver loja pública"
              >
                <ShoppingBag className="w-4 h-4" />
                <span className="hidden lg:inline text-xs">Loja</span>
              </Button>
            )}
            <WalkInButton salonId={meData?.salon?.id} isMobile={isMobile} />
            <SalonNotificationBell />
          </div>
        </div>
        <ImpersonationBanner />
        <TrialExpiryBanner salon={meData?.salon} />
        {isMobile && <MobileNetworkRecoveryBanner />}
        {isMobile && <MobileContextActions location={location} setLocation={setLocation} staffRole={staffRole} />}
        {isMobile && <MobileOperationalAlerts staffRole={staffRole} setLocation={setLocation} />}
        <SwipeableMain
          isMobile={isMobile}
          visibleMenuItems={visibleMenuItems}
          location={location}
          setLocation={setLocation}
          tutorialResume={{
            available: hasFeature("hasAcademy"),
            hasProgress: hasTutorialProgress,
            isLoading: tutorialProgressQuery.isLoading,
            onOpen: openTutorialContinuation,
          }}
        >
          {children}
        </SwipeableMain>
        {/* Bottom Navigation Bar — apenas mobile (< 768px) */}
        {isMobile && (
          <MobileBottomNav
            visibleItems={visibleMenuItems}
            location={location}
            setLocation={setLocation}
            staffRole={staffRole}
            hasFeature={hasFeature}
            user={user}
            logout={logout}
            salonId={meData?.salon?.id}
            accessibilityPreferences={accessibilityPreferences}
            onToggleLargeText={toggleLargeText}
            onToggleHighContrast={toggleHighContrast}
          />
        )}
      </SidebarInset>
    </>
  );
}

function MobileOperationalAlerts({ staffRole, setLocation }: { staffRole: StaffRole; setLocation: (path: string) => void }) {
  const shouldLoadOperationalAlerts = staffRole !== "funcionario";
  const todayRange = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    end.setMilliseconds(-1);
    return { startDate: start, endDate: end };
  }, []);
  const { data: queue = [] } = trpc.queue.list.useQuery(undefined, {
    enabled: shouldLoadOperationalAlerts,
    refetchInterval: 15_000,
  });
  const { data: appointments = [] } = trpc.appointments.getByDateRange.useQuery(todayRange, {
    enabled: shouldLoadOperationalAlerts,
    refetchInterval: 60_000,
  });
  const alerts = useMemo(
    () => getMobileOperationalAlerts({ staffRole, queue: queue as any[], appointments: appointments as any[] }),
    [staffRole, queue, appointments],
  );

  if (alerts.length === 0) return null;

  return (
    <div className="border-b bg-background px-3 py-2">
      <div className="flex gap-2 overflow-x-auto pb-0.5">
        {alerts.map((alert: any) => (
          <button
            key={alert.id}
            type="button"
            onClick={() => setLocation(alert.path)}
            className={`min-w-[238px] shrink-0 rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              alert.tone === "danger" ? "border-red-200 bg-red-50 text-red-950" : alert.tone === "warning" ? "border-amber-200 bg-amber-50 text-amber-950" : "border-primary/20 bg-primary/5 text-foreground"
            }`}
          >
            <span className="flex items-center gap-2 text-xs font-semibold"><AlertTriangle className="h-3.5 w-3.5" />{alert.title}</span>
            <span className="mt-0.5 block text-[11px] opacity-80">{alert.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MobileContextActions({ location, setLocation, staffRole }: { location: string; setLocation: (path: string) => void; staffRole: StaffRole }) {
  const isOperationalRoute = ["/weekly-calendar", "/appointments", "/checkout", "/painel-chamada"].some((path) => location.startsWith(path));
  if (!isOperationalRoute) return null;
  const actions = location.startsWith("/checkout")
    ? [{ label: "Agenda", path: "/weekly-calendar", icon: CalendarDays }, { label: "Fila", path: "/painel-chamada", icon: Users }, { label: "Clientes", path: "/clients", icon: UserCircle2 }]
    : location.startsWith("/painel-chamada")
      ? [{ label: "Agenda", path: "/weekly-calendar", icon: CalendarDays }, { label: "Caixa", path: "/checkout", icon: CreditCard }, { label: "Clientes", path: "/clients", icon: UserCircle2 }]
      : [{ label: "Agenda do dia", path: "/appointments", icon: CalendarDays }, { label: "Caixa", path: "/checkout", icon: CreditCard }, { label: "Fila", path: "/painel-chamada", icon: Users }];
  const allowed = actions.filter((action) => !(staffRole === "funcionario" && ["/checkout", "/painel-chamada"].includes(action.path)));
  return <div className="border-b bg-muted/25 px-3 py-2"><div className="flex gap-2 overflow-x-auto pb-0.5">{allowed.map((action) => { const Icon = action.icon; return <Button key={action.path} size="sm" variant={location.startsWith(action.path) ? "default" : "outline"} className="h-11 shrink-0 gap-2 px-3 text-sm" onClick={() => setLocation(action.path)}><Icon className="h-4 w-4" />{action.label}</Button>; })}</div></div>;
}

// ─── Swipeable Main Content ─────────────────────────────────────────────────────
// Detecta swipe horizontal e navega entre as seções da bottom nav
function SwipeableMain({
  isMobile,
  visibleMenuItems,
  location,
  setLocation,
  tutorialResume,
  children,
}: {
  isMobile: boolean;
  visibleMenuItems: typeof menuItems;
  location: string;
  setLocation: (path: string) => void;
  tutorialResume: { available: boolean; hasProgress: boolean; isLoading: boolean; onOpen: () => void };
  children: React.ReactNode;
}) {
  // Todas as seções navegáveis (bottom nav + mais) na ordem do menu
  const navPaths = visibleMenuItems.map(i => i.path);
  const currentIndex = navPaths.indexOf(location);

  const goNext = useCallback(() => {
    if (currentIndex < navPaths.length - 1) {
      setLocation(navPaths[currentIndex + 1]);
    }
  }, [currentIndex, navPaths, setLocation]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setLocation(navPaths[currentIndex - 1]);
    }
  }, [currentIndex, navPaths, setLocation]);

  const [slideDir, setSlideDir] = useState<"left" | "right" | null>(null);
  const [animKey, setAnimKey] = useState(0);

  const triggerAnim = useCallback((dir: "left" | "right", navigate: () => void) => {
    setSlideDir(dir);
    setAnimKey(k => k + 1);
    navigate();
    // Limpa a classe após a animação
    setTimeout(() => setSlideDir(null), 300);
  }, []);

  // Desabilita swipe de abas na agenda semanal — o scroll horizontal pertence ao calendário
  const isCalendarRoute = location === "/weekly-calendar" || location.startsWith("/weekly-calendar");
  const swipeRef = useSwipeNavigation({
    enabled: isMobile && !isCalendarRoute,
    threshold: 70,
    velocityThreshold: 0.4,
    onSwipeLeft: () => triggerAnim("left", goNext),
    onSwipeRight: () => triggerAnim("right", goPrev),
  });

  const slideClass = slideDir === "left"
    ? "animate-slide-in-left"
    : slideDir === "right"
    ? "animate-slide-in-right"
    : "";

  // Na rota do calendário: sem padding, ocupa toda a altura disponível para scroll interno
  const isFullscreen = isCalendarRoute;
  return (
    <main
      ref={swipeRef as React.RefObject<HTMLElement>}
      className={isFullscreen
        ? `flex-1 flex flex-col overflow-hidden ${isMobile ? "pb-24" : ""}`
        : `flex-1 overflow-y-auto p-3 md:p-4 lg:p-6 ${isMobile ? "pb-24" : ""}`
      }
    >
      <div
        key={animKey}
        className={[
          isMobile && slideClass ? slideClass : "",
          isFullscreen ? "flex flex-col flex-1 overflow-hidden" : "",
        ].filter(Boolean).join(" ")}
      >
        {location === "/" && tutorialResume.available && (
          <section className="mb-3 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50/80 p-3 shadow-sm sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white"><BookOpen className="h-5 w-5" /></span><div className="min-w-0"><p className="text-sm font-semibold text-amber-950">{tutorialResume.hasProgress ? "Continue seu tutorial" : "Comece a configuração inicial"}</p><p className="mt-0.5 text-xs text-amber-900">{tutorialResume.hasProgress ? "Volte direto para a próxima seção do seu perfil." : "Veja os primeiros passos na ordem certa para o seu trabalho."}</p></div></div>
            <Button className="min-h-11 shrink-0 gap-2 bg-amber-600 hover:bg-amber-700" onClick={tutorialResume.onOpen} disabled={tutorialResume.isLoading}><BookOpen className="h-4 w-4" />{tutorialResume.hasProgress ? "Retomar tutorial" : "Ver primeiros passos"}</Button>
          </section>
        )}
        {children}
      </div>
    </main>
  );
}

// ─── Bottom Navigation Bar (Mobile) ───────────────────────────────────────────
// Atalhos de uma mão: cada cargo vê primeiro as tarefas do próprio turno.
const MOBILE_NAV_PATHS_BY_ROLE: Record<StaffRole, string[]> = {
  admin: ["/weekly-calendar", "/checkout", "/clients"],
  recepcionista: ["/weekly-calendar", "/appointments", "/checkout"],
  caixa: ["/checkout", "/weekly-calendar", "/products", "/clock-in"],
  funcionario: ["/weekly-calendar", "/clock-in", "/my-commissions"],
};

const MOBILE_QUICK_ACTION_ICONS: Record<MobileQuickActionIcon, React.ElementType> = {
  calendar: CalendarDays,
  payment: CreditCard,
  client: UserCircle2,
  queue: Users,
  clock: Clock,
  commission: Percent,
  product: Package,
};

function MobileQuickActionButton({
  salonId,
  staffRole,
  visibleItems,
  onNavigate,
}: {
  salonId?: number;
  staffRole: StaffRole;
  visibleItems: typeof menuItems;
  onNavigate: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [checkInQrOpen, setCheckInQrOpen] = useState(false);
  const actions = getMobileQuickActions(staffRole, visibleItems.map((item) => item.path));
  const supportsWalkIn = staffRole === "admin" && !!salonId;
  const supportsCheckIn = (staffRole === "admin" || staffRole === "recepcionista") && !!salonId;

  return <>
    <button onClick={() => setOpen(true)} className="relative -mt-6 flex flex-1 flex-col items-center justify-center gap-0.5 text-primary" aria-label="Abrir ações do turno">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-background bg-primary text-primary-foreground shadow-lg transition-transform active:scale-95"><Zap className="h-6 w-6" /></span>
      <span className="text-[11px] font-semibold leading-none">Ação</span>
    </button>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
        <SheetHeader className="text-left"><SheetTitle className="flex items-center gap-2 text-base"><Zap className="h-4 w-4 text-primary" />Ações do seu turno</SheetTitle></SheetHeader>
        <p className="mt-1 text-sm text-muted-foreground">Atalhos organizados para a sua função, sem precisar procurar no menu.</p>
        <div className="mt-4 grid gap-2">
          {actions.map((action) => { const Icon = MOBILE_QUICK_ACTION_ICONS[action.icon]; return <button key={action.path} type="button" onClick={() => { setOpen(false); onNavigate(action.path); }} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3.5 text-left transition-colors active:bg-accent/50"><span className={`flex h-9 w-9 items-center justify-center rounded-lg ${action.tone}`}><Icon className="h-4 w-4" /></span><span className="min-w-0"><span className="block text-sm font-medium">{action.label}</span><span className="block text-xs text-muted-foreground">{action.description}</span></span></button>; })}
          {supportsCheckIn && <Button variant="outline" className="h-11 w-full gap-2" onClick={() => { setOpen(false); onNavigate(`/checkin/${salonId}`); }}><UserCheck className="h-4 w-4" />Registrar chegada</Button>}
          {supportsCheckIn && <Button variant="outline" className="h-11 w-full gap-2" onClick={() => { setOpen(false); setCheckInQrOpen(true); }}><QrCode className="h-4 w-4" />Exibir QR de chegada</Button>}
          {supportsWalkIn && <Button className="mt-1 h-11 w-full gap-2" onClick={() => { setOpen(false); setWalkInOpen(true); }}><Zap className="h-4 w-4" />Atendimento rápido de balcão</Button>}
        </div>
      </SheetContent>
    </Sheet>
    <Dialog open={checkInQrOpen} onOpenChange={setCheckInQrOpen}>
      <DialogContent className="max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle>QR de chegada</DialogTitle>
          <DialogDescription>Mostre este QR para a cliente. Ela abre o check-in do salão e confirma o telefone para validar o agendamento de hoje.</DialogDescription>
        </DialogHeader>
        {salonId && typeof window !== "undefined" && (
          <div className="flex flex-col items-center gap-3 rounded-xl bg-muted/50 p-5">
            <QRCodeSVG value={`${window.location.origin}/checkin/${salonId}`} size={220} level="M" includeMargin />
            <p className="text-center text-xs text-muted-foreground">O QR não cria agendamento e só confirma chegadas de reservas válidas para hoje.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <WalkInModal open={walkInOpen} onClose={() => setWalkInOpen(false)} salonId={salonId} />
  </>;
}

function MobileMoreMenuSection({
  group,
  location,
  hasFeature,
  onNavigate,
}: {
  group: MobileMoreMenuGroup & { items: typeof menuItems };
  location: string;
  hasFeature: (key: keyof SalonFeaturesType) => boolean;
  onNavigate: (path: string) => void;
}) {
  const GroupIcon = group.icon;
  const headingId = `more-menu-group-${group.id}`;

  return (
    <section aria-labelledby={headingId}>
      <div className="mb-2 flex items-center gap-2 px-1 text-sm font-semibold text-muted-foreground">
        <GroupIcon className="h-4 w-4" aria-hidden="true" />
        <h3 id={headingId}>{group.label}</h3>
      </div>
      <div className="grid gap-2">
        {group.items.map(item => {
          const isActive = location === item.path;
          const isLocked = item.featureKey ? !hasFeature(item.featureKey) : false;
          const ItemIcon = item.icon;
          return (
            <button
              key={item.path}
              type="button"
              onClick={() => !isLocked && onNavigate(item.path)}
              aria-disabled={isLocked}
              aria-label={isLocked ? `${item.label} — indisponível no seu plano` : item.label}
              className={`relative flex min-h-[56px] w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                isActive
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : "border-border bg-card text-foreground hover:bg-accent active:bg-accent/70"
              } ${isLocked ? "cursor-not-allowed opacity-50" : ""}`}
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isActive ? "bg-primary/15" : "bg-muted"}`}>
                <ItemIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1 text-sm font-semibold leading-5">{item.label}</span>
              {isLocked && (
                <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Plano</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function MobileBottomNav({
  visibleItems,
  location,
  setLocation,
  staffRole,
  hasFeature,
  user,
  logout,
  salonId,
  accessibilityPreferences,
  onToggleLargeText,
  onToggleHighContrast,
}: {
  visibleItems: typeof menuItems;
  location: string;
  setLocation: (path: string) => void;
  staffRole: StaffRole;
  hasFeature: (key: keyof SalonFeaturesType) => boolean;
  user: { name?: string | null; email?: string | null } | null;
  logout: () => void;
  salonId?: number;
  accessibilityPreferences: AccessibilityPreferences;
  onToggleLargeText: () => void;
  onToggleHighContrast: () => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const trackExperience = trpc.experience.track.useMutation();

  // Itens fixos da bottom bar (filtra pelos visíveis para o role)
  const bottomNavPaths = MOBILE_NAV_PATHS_BY_ROLE[staffRole] ?? MOBILE_NAV_PATHS_BY_ROLE.admin;
  const bottomItems = bottomNavPaths
    .map(p => visibleItems.find(i => i.path === p))
    .filter(Boolean) as typeof menuItems;

  // A origem continua sendo visibleItems, que já aplica cargo, permissões e feature locks.
  const moreItems = visibleItems.filter(i => !bottomNavPaths.includes(i.path));
  const groupedMoreItems = MOBILE_MORE_MENU_GROUPS
    .map(group => ({ ...group, items: moreItems.filter(item => group.paths.includes(item.path)) }))
    .filter(group => group.items.length > 0);
  const groupedPaths = new Set(groupedMoreItems.flatMap(group => group.items.map(item => item.path)));
  const ungroupedMoreItems = moreItems.filter(item => !groupedPaths.has(item.path));

  const navigate = (path: string) => {
    trackExperience.mutate({ eventKey: "mobile_shortcut_opened", surface: "mobile_bottom_nav" });
    setLocation(path);
    setSheetOpen(false);
  };

  return (
    <>
      {/* Bottom Nav Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border safe-area-pb">
        <div className="flex items-stretch h-[68px]">
          {bottomItems.map(item => {
            const isActive = location === item.path;
            const isLocked = item.featureKey ? !hasFeature(item.featureKey) : false;
            return (
              <button
                key={item.path}
                onClick={() => !isLocked && navigate(item.path)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-all duration-200 relative ${
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground active:text-foreground"
                } ${isLocked ? "opacity-40" : ""}`}
              >
                {isActive && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />
                )}
                <item.icon className={`h-6 w-6 transition-transform duration-200 ${isActive ? "scale-110" : ""}`} />
                <span className={`text-[11px] font-medium leading-none truncate max-w-[64px] ${isActive ? "font-semibold" : ""}`}>
                  {item.label.length > 9 ? item.label.split(" ")[0] : item.label}
                </span>
              </button>
            );
          })}

          <MobileQuickActionButton salonId={salonId} staffRole={staffRole} visibleItems={visibleItems} onNavigate={navigate} />

          {/* Botão "Mais" */}
          <button
            onClick={() => setSheetOpen(true)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-all duration-200 ${
              sheetOpen ? "text-primary" : "text-muted-foreground"
            } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring`}
            aria-label="Abrir mais opções do menu"
            aria-expanded={sheetOpen}
            aria-haspopup="dialog"
          >
            <MoreHorizontal className="h-6 w-6" />
            <span className="text-[11px] font-medium leading-none">Mais</span>
          </button>
        </div>
      </nav>

      {/* Sheet "Mais" — menu completo */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto pb-safe">
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-lg font-bold">Menu</SheetTitle>
              <div className="flex items-center gap-2.5">
                <Avatar className="h-9 w-9 border">
                  <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="text-sm font-semibold leading-none">{user?.name}</p>
                  <p className="text-xs text-muted-foreground mt-1 truncate max-w-[160px]">{user?.email}</p>
                </div>
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-5 py-4" aria-label="Opções do menu">
            {groupedMoreItems.map(group => (
              <MobileMoreMenuSection
                key={group.id}
                group={group}
                location={location}
                hasFeature={hasFeature}
                onNavigate={navigate}
              />
            ))}
            {ungroupedMoreItems.length > 0 && (
              <MobileMoreMenuSection
                group={{ id: "other", label: "Outros recursos", icon: MoreHorizontal, paths: [], items: ungroupedMoreItems }}
                location={location}
                hasFeature={hasFeature}
                onNavigate={navigate}
              />
            )}
          </div>

          <section className="border-t py-4" aria-labelledby="accessibility-preferences-title">
            <div className="mb-3 flex items-center gap-2 px-1">
              <Settings className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <div>
                <h3 id="accessibility-preferences-title" className="text-sm font-semibold">Acessibilidade</h3>
                <p className="text-xs text-muted-foreground">Ajustes salvos neste dispositivo.</p>
              </div>
            </div>
            <div className="grid gap-2">
              <button
                type="button"
                onClick={onToggleLargeText}
                aria-pressed={accessibilityPreferences.largeText}
                className={`flex min-h-[56px] w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  accessibilityPreferences.largeText ? "border-primary/30 bg-primary/10" : "border-border bg-card hover:bg-accent active:bg-accent/70"
                }`}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted"><Type className="h-5 w-5" aria-hidden="true" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">Texto maior</span><span className="block text-xs text-muted-foreground">Amplia a leitura em todo o aplicativo.</span></span>
                <span className="text-xs font-semibold text-muted-foreground">{accessibilityPreferences.largeText ? "Ativado" : "Desativado"}</span>
              </button>
              <button
                type="button"
                onClick={onToggleHighContrast}
                aria-pressed={accessibilityPreferences.highContrast}
                className={`flex min-h-[56px] w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  accessibilityPreferences.highContrast ? "border-primary/30 bg-primary/10" : "border-border bg-card hover:bg-accent active:bg-accent/70"
                }`}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted"><Contrast className="h-5 w-5" aria-hidden="true" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">Alto contraste</span><span className="block text-xs text-muted-foreground">Aumenta o contraste visual em todo o aplicativo.</span></span>
                <span className="text-xs font-semibold text-muted-foreground">{accessibilityPreferences.highContrast ? "Ativado" : "Desativado"}</span>
              </button>
            </div>
          </section>

          {/* Rodapé do sheet */}
          <div className="border-t pt-3 mt-1">
            <button
              onClick={() => { logout(); setSheetOpen(false); }}
              className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="text-sm font-medium">Sair</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
